import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

export interface ProductGroup {
  id: number;
  descricao?: string;
  referencia?: string;
  isInativo: boolean;
  mostrarPdv: boolean;
  mostrarCelular: boolean;
  mostrarCatalogo: boolean;
  mostrarEcommerce: boolean;
  idPos?: string;
}

export interface ProductGroupFormData {
  descricao?: string;
  referencia?: string;
  isInativo?: boolean;
  mostrarPdv?: boolean;
  mostrarCelular?: boolean;
  mostrarCatalogo?: boolean;
  mostrarEcommerce?: boolean;
  idPos?: string;
}

export function useProductGroups() {
  const queryClient = useQueryClient();

  const { data: groups = [], isLoading } = useQuery({
    queryKey: ["product-groups"],
    queryFn: async () => {
      const { data } = await apiClient.get<ProductGroup[]>("/api/grupos-produto");
      return data;
    },
  });

  const createGroup = useMutation({
    mutationFn: async (formData: ProductGroupFormData) => {
      const { data } = await apiClient.post<ProductGroup>("/api/grupos-produto", formData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-groups"] });
      toast.success("Grupo criado com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao criar grupo: " + error.message);
    },
  });

  const updateGroup = useMutation({
    mutationFn: async ({ id, ...formData }: ProductGroupFormData & { id: number }) => {
      const { data } = await apiClient.put<ProductGroup>(`/api/grupos-produto/${id}`, formData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-groups"] });
      toast.success("Grupo atualizado com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao atualizar grupo: " + error.message);
    },
  });

  const deleteGroup = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/api/grupos-produto/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-groups"] });
      toast.success("Grupo inativado com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao inativar grupo: " + error.message);
    },
  });

  return {
    groups,
    isLoading,
    createGroup,
    updateGroup,
    deleteGroup,
  };
}
